import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useAuth } from '../context/AuthContext';
// Remove unused imports for site/role fetching services
// import { getPublicSites, getPublicUnits } from '../services/siteService'; 
// import { getRegistrationRoles } from '../services/authService';
import Button from '../components/common/Button';
// Remove unused sort utils
// import { alphabeticalSort, naturalSort } from '../utils/sortUtils';

// Validation schema - Site/Unit validation might need adjustment if IDs are always present now
const RegisterSchema = Yup.object().shape({
  firstName: Yup.string()
    .required('First name is required')
    .min(2, 'First name must be at least 2 characters'),
  lastName: Yup.string()
    .required('Last name is required')
    .min(2, 'Last name must be at least 2 characters'),
  email: Yup.string()
    .email('Invalid email address')
    .required('Email is required'),
  password: Yup.string()
    .required('Password is required')
    .min(6, 'Password must be at least 6 characters'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password'), null], 'Passwords must match')
    .required('Confirm password is required'),
  cellphone: Yup.string()
    .required('Mobile number is required')
    .matches(/^[0-9+\s-]+$/, 'Invalid mobile number format'), 
  // Keep siteUuid and unitId required for structure, but they'll be pre-filled
  siteUuid: Yup.string()
    .required('Site UUID is required'), 
  siteName: Yup.string()
    .required('Site name is required'),
  unitId: Yup.string()
    .required('Unit ID is required'),
  selectedRole: Yup.string()
    .required('Role selection is required'),
});

function OperatorManagerRegister() {
  // Use the new registerInternal function from the context
  const { registerInternal, loading } = useAuth(); 
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  // Remove state related to fetching sites, units, roles
  // const [sites, setSites] = useState([]); 
  // const [units, setUnits] = useState([]); 
  // const [roles, setRoles] = useState([]);
  // const [sitesLoading, setSitesLoading] = useState(false);
  // const [unitsLoading, setUnitsLoading] = useState(false);
  // const [rolesLoading, setRolesLoading] = useState(false);
  // const [defaultSiteUuid, setDefaultSiteUuid] = useState('');
  // const [defaultUnitId, setDefaultUnitId] = useState('');

  // Remove useEffect for fetching sites/units
  // useEffect(() => { ... }, []);

  // Remove useEffect for fetching roles
  // useEffect(() => { ... }, []);

  // Placeholder constants - *** REPLACE THESE WITH ACTUAL VALUES ***
  const LAB_SITE_UUID = 'F38239A3-E6B1-4A3B-9EB6-CD39D2537B22';
  const LAB_UNIT_1_ID = '1'; 

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setError(null);
      const { confirmPassword, ...userData } = values;
      
      // Ensure correct hardcoded values are sent
      const registrationData = {
        ...userData,
        siteUuid: LAB_SITE_UUID, // Use the constant
        siteName: 'Lab', 
        unitId: LAB_UNIT_1_ID, // Use the constant
        unitDisplayName: '1', 
        selectedRole: 'OPERATOR' // Use the hardcoded role value
      };
      
      // Remove selectedRole from the final payload if the API expects role name directly 
      // Or adjust if the API expects a different structure for the role
      // delete registrationData.selectedRole; // Example if needed

      // console.log("Submitting internal registration data:", registrationData); // Debug log

      // Call the internal registration function
      const response = await registerInternal(registrationData);
      
      navigate('/pending-approval', { 
        state: { 
          userInfo: response.user || {
            ...registrationData, // Pass the submitted data
            siteName: "Lab", // Ensure consistent display
            unitDisplayName: "1" 
          }
        }
      });
    } catch (err) {
      // console.error("Registration Submit Error:", err); // Log the full error
      setError(err.response?.data?.userMessage || err.response?.data?.message || 'Registration failed. Please try again.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSubmitting(false);
    }
  };

  // Remove the initial loading return block
  // if (sitesLoading || unitsLoading || !defaultSiteUuid || !defaultUnitId) { ... }

  return (
    <div className="max-w-md mx-auto mt-8 mb-12">
      <h1 className="text-3xl font-bold text-center mb-6 text-hooray-red font-mont">Internal Account Creation (Lab)</h1>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4" 
             style={{ whiteSpace: 'pre-line' }}>
          {error}
        </div>
      )}
      
      <div className="bg-white rounded-lg shadow-md p-6">
        <Formik
          initialValues={{ 
            firstName: '',
            lastName: '', 
            email: '', 
            password: '', 
            confirmPassword: '',
            cellphone: '',
            // Use placeholder constants for initial IDs
            siteUuid: LAB_SITE_UUID, 
            siteName: 'Lab',
            unitId: LAB_UNIT_1_ID, 
            selectedRole: 'OPERATOR' // Pre-fill role value
          }}
          validationSchema={RegisterSchema}
          onSubmit={handleSubmit}
          // enableReinitialize might not be needed anymore without async defaults
        >
          {({ isSubmitting, touched, errors, values }) => ( 
            <Form>
              {/* ... Keep First Name, Last Name, Email, Cellphone fields ... */}
              <div className="mb-4">
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                  First Name
                </label>
                <Field
                  type="text"
                  name="firstName"
                  id="firstName"
                  className={`w-full px-3 py-2 border rounded-md ${
                    touched.firstName && errors.firstName ? 'border-red-500' : 'border-gray-300'
                  } focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent`}
                />
                <ErrorMessage name="firstName" component="div" className="text-red-500 text-sm mt-1" />
              </div>
              
              <div className="mb-4">
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name
                </label>
                <Field
                  type="text"
                  name="lastName"
                  id="lastName"
                  className={`w-full px-3 py-2 border rounded-md ${
                    touched.lastName && errors.lastName ? 'border-red-500' : 'border-gray-300'
                  } focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent`}
                />
                <ErrorMessage name="lastName" component="div" className="text-red-500 text-sm mt-1" />
              </div>
              
              <div className="mb-4">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <Field
                  type="email"
                  name="email"
                  id="email"
                  className={`w-full px-3 py-2 border rounded-md ${
                    touched.email && errors.email ? 'border-red-500' : 'border-gray-300'
                  } focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent`}
                />
                <ErrorMessage name="email" component="div" className="text-red-500 text-sm mt-1" />
              </div>
              
              <div className="mb-4">
                <label htmlFor="cellphone" className="block text-sm font-medium text-gray-700 mb-1">
                  Mobile Number
                </label>
                <Field
                  type="text"
                  name="cellphone"
                  id="cellphone"
                  placeholder="e.g. 082 456 7890"
                  className={`w-full px-3 py-2 border rounded-md ${
                    touched.cellphone && errors.cellphone ? 'border-red-500' : 'border-gray-300'
                  } focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent`}
                />
                <ErrorMessage name="cellphone" component="div" className="text-red-500 text-sm mt-1" />
              </div>

              {/* Display Site and Unit as static text */}
              <div className="mb-4">
                <p className="block text-sm font-medium text-gray-700 mb-1">Site</p>
                <p className="w-full px-3 py-2 border rounded-md bg-gray-100 text-gray-600">Lab</p>
                 <Field type="hidden" name="siteUuid" />
                 <Field type="hidden" name="siteName" />
                 {/* Add warning if placeholder is still used */} 
                 {LAB_SITE_UUID === 'UNKNOWN_LAB_UUID' && 
                    <div className="text-xs text-orange-600 mt-1">Warning: Lab Site UUID not configured.</div>
                 }
              </div>

              <div className="mb-4">
                 <p className="block text-sm font-medium text-gray-700 mb-1">Unit</p>
                 <p className="w-full px-3 py-2 border rounded-md bg-gray-100 text-gray-600">1</p>
                 <Field type="hidden" name="unitId" />
                 {/* Add warning if placeholder is still used */} 
                 {LAB_UNIT_1_ID === 'UNKNOWN_UNIT_1_ID' && 
                     <div className="text-xs text-orange-600 mt-1">Warning: Lab Unit 1 ID not configured.</div>
                 }
              </div>
              
              {/* Role - Display static text, submit hidden value */}
              <div className="mb-4">
                <p className="block text-sm font-medium text-gray-700 mb-1">Role</p>
                <p className="w-full px-3 py-2 border rounded-md bg-gray-100 text-gray-600">Operator</p>
                <Field type="hidden" name="selectedRole" />
                {/* Remove role dropdown and associated loading/error messages */}
              </div>
              
              {/* ... Keep Password and Confirm Password fields ... */}
               <div className="mb-4">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <Field
                  type="password"
                  name="password"
                  id="password"
                  className={`w-full px-3 py-2 border rounded-md ${
                    touched.password && errors.password ? 'border-red-500' : 'border-gray-300'
                  } focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent`}
                />
                <ErrorMessage name="password" component="div" className="text-red-500 text-sm mt-1" />
              </div>
              
              <div className="mb-6">
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm Password
                </label>
                <Field
                  type="password"
                  name="confirmPassword"
                  id="confirmPassword"
                  className={`w-full px-3 py-2 border rounded-md ${
                    touched.confirmPassword && errors.confirmPassword ? 'border-red-500' : 'border-gray-300'
                  } focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent`}
                />
                <ErrorMessage name="confirmPassword" component="div" className="text-red-500 text-sm mt-1" />
              </div>
              
              {/* Submit button - remove dependency on default IDs loading */}
              <Button
                type="submit"
                variant="cta"
                className="w-full"
                disabled={isSubmitting || loading || LAB_SITE_UUID === 'UNKNOWN_LAB_UUID' || LAB_UNIT_1_ID === 'UNKNOWN_UNIT_1_ID'} 
              >
                {isSubmitting || loading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating account...
                  </span>
                ) : (
                  'Create Account' 
                )}
              </Button>
              {(LAB_SITE_UUID === 'UNKNOWN_LAB_UUID' || LAB_UNIT_1_ID === 'UNKNOWN_UNIT_1_ID') && (
                <div className="text-center text-sm text-red-600 mt-2">Registration disabled until Site UUID and Unit ID are configured in the code.</div>
              )}
            </Form>
          )}
        </Formik>
        
        <div className="mt-4 text-center">
          <p className="text-sm text-gray-600">
            Already have an account?{' '}
            <Link to="/login" className="text-hooray-red hover:text-hooray-yellow font-medium">
              Login here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default OperatorManagerRegister; 